<template>
  <Teleport to="body">
    <Transition name="context-panel">
      <div 
        v-if="isVisible && currentTool !== 'select'"
        ref="panelRef"
        class="annotation-context-panel"
        :style="panelPosition"
        :class="panelClasses"
      >
        <!-- Panel Header -->
        <div class="context-panel__header">
          <div class="context-panel__title">
            <i :class="getToolIcon(currentTool)" class="context-panel__icon"></i>
            <span>{{ getToolLabel(currentTool) }}</span>
          </div>
          <button 
            class="context-panel__close"
            @click="handleClose"
            :title="$t('common.close') || 'Close'"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Panel Content - Different sections based on active tool -->
        <div class="context-panel__content">
          
          <!-- Stroke/Drawing Settings (for drawing tools) -->
          <div 
            v-if="isDrawingTool" 
            class="context-panel__section"
          >
            <div class="context-panel__section-title">
              {{ $t('annotation.strokeSettings') || 'Stroke Settings' }}
            </div>

            <!-- Stroke Width -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.strokeWidth') || 'Width' }}
              </label>
              <div class="control-with-value">
                <input
                  type="range"
                  min="1"
                  max="20"
                  :value="settings.strokeWidth"
                  @input="handleStrokeWidthChange"
                  class="slider"
                />
                <div class="control-value-display">
                  <button 
                    class="value-btn value-btn--minus"
                    @click="adjustStrokeWidth(-1)"
                    :disabled="settings.strokeWidth <= 1"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                  <span class="value-text">{{ settings.strokeWidth }}px</span>
                  <button 
                    class="value-btn value-btn--plus"
                    @click="adjustStrokeWidth(1)"
                    :disabled="settings.strokeWidth >= 20"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Opacity -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.opacity') || 'Opacity' }}
              </label>
              <div class="control-with-value">
                <input
                  type="range"
                  min="0"
                  max="100"
                  :value="settings.opacity * 100"
                  @input="handleOpacityChange"
                  class="slider"
                />
                <div class="control-value-display">
                  <button 
                    class="value-btn value-btn--minus"
                    @click="adjustOpacity(-10)"
                    :disabled="settings.opacity <= 0"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                  <span class="value-text">{{ Math.round(settings.opacity * 100) }}%</span>
                  <button 
                    class="value-btn value-btn--plus"
                    @click="adjustOpacity(10)"
                    :disabled="settings.opacity >= 1"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
              <!-- Opacity preview -->
              <div class="opacity-preview" :style="{ opacity: settings.opacity, backgroundColor: settings.strokeColor }"></div>
            </div>

            <!-- Color -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.color') || 'Color' }}
              </label>
              <div class="color-display">
                <div 
                  class="color-swatch-large" 
                  :style="{ backgroundColor: settings.strokeColor }"
                  @click="$emit('open-color-picker')"
                  :title="$t('annotation.selectColor') || 'Click to change color'"
                >
                  <i class="fas fa-palette color-swatch-icon"></i>
                </div>
                <span class="color-value">{{ settings.strokeColor }}</span>
              </div>
            </div>
          </div>

          <!-- Text Settings (for text tool) -->
          <div 
            v-if="currentTool === 'text'" 
            class="context-panel__section"
          >
            <div class="context-panel__section-title">
              {{ $t('annotation.textSettings') || 'Text Settings' }}
            </div>

            <!-- Font Size -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.fontSize') || 'Font Size' }}
              </label>
              <div class="control-with-value">
                <input
                  type="range"
                  min="8"
                  max="72"
                  :value="settings.fontSize"
                  @input="handleFontSizeChange"
                  class="slider"
                />
                <div class="control-value-display">
                  <button 
                    class="value-btn value-btn--minus"
                    @click="adjustFontSize(-2)"
                    :disabled="settings.fontSize <= 8"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                  <input
                    type="number"
                    min="8"
                    max="72"
                    :value="settings.fontSize"
                    @input="handleFontSizeChange"
                    class="value-input"
                  />
                  <button 
                    class="value-btn value-btn--plus"
                    @click="adjustFontSize(2)"
                    :disabled="settings.fontSize >= 72"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Color -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.color') || 'Color' }}
              </label>
              <div class="color-display">
                <div 
                  class="color-swatch-large" 
                  :style="{ backgroundColor: settings.strokeColor }"
                  @click="$emit('open-color-picker')"
                  :title="$t('annotation.selectColor') || 'Click to change color'"
                >
                  <i class="fas fa-palette color-swatch-icon"></i>
                </div>
                <span class="color-value">{{ settings.strokeColor }}</span>
              </div>
            </div>
          </div>

          <!-- Measurement Settings (for measurement tools) -->
          <div 
            v-if="isMeasurementTool" 
            class="context-panel__section"
          >
            <div class="context-panel__section-title">
              {{ $t('annotation.measurementSettings') || 'Measurement Settings' }}
            </div>

            <!-- Scale -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.scale') || 'Scale' }}
              </label>
              <div class="scale-input-wrapper">
                <span class="scale-prefix">1:</span>
                <input
                  type="number"
                  :value="getCurrentScaleValue()"
                  @input="handleCustomScaleInput"
                  @blur="handleCustomScaleBlur"
                  @keypress.enter="handleCustomScaleBlur"
                  class="scale-input"
                  min="1"
                  step="1"
                  placeholder="100"
                  :title="$t('annotation.scaleRatio') || 'Scale ratio (e.g., 100 for 1:100)'"
                />
              </div>
              <!-- Quick presets -->
              <div class="scale-presets">
                <button
                  v-for="preset in scalePresets"
                  :key="preset"
                  class="preset-btn"
                  :class="{ active: getCurrentScaleValue() === preset }"
                  @click="setScale(preset)"
                >
                  1:{{ preset }}
                </button>
              </div>
            </div>

            <!-- Unit -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.unit') || 'Unit' }}
              </label>
              <div class="unit-selector">
                <button
                  v-for="unit in measurementUnits"
                  :key="unit.value"
                  class="unit-btn"
                  :class="{ active: (settings.measurementUnit || 'meters') === unit.value }"
                  @click="handleUnitChange(unit.value)"
                  :title="unit.label"
                >
                  {{ unit.short }}
                </button>
              </div>
            </div>

            <!-- Calibrate Button -->
            <div class="control-group">
              <button
                class="btn-calibrate"
                @click="$emit('calibrate-scale')"
                :title="$t('annotation.calibrateScale') || 'Calibrate scale with known distance'"
              >
                <i class="fas fa-ruler-combined"></i>
                {{ $t('annotation.calibrate') || 'Calibrate' }}
              </button>
            </div>

            <!-- Stroke Width for measurement lines -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.lineWidth') || 'Line Width' }}
              </label>
              <div class="control-with-value">
                <input
                  type="range"
                  min="1"
                  max="10"
                  :value="settings.strokeWidth"
                  @input="handleStrokeWidthChange"
                  class="slider"
                />
                <span class="value-text-inline">{{ settings.strokeWidth }}px</span>
              </div>
            </div>

            <!-- Color -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.color') || 'Color' }}
              </label>
              <div class="color-display">
                <div 
                  class="color-swatch-large" 
                  :style="{ backgroundColor: settings.strokeColor }"
                  @click="$emit('open-color-picker')"
                  :title="$t('annotation.selectColor') || 'Click to change color'"
                >
                  <i class="fas fa-palette color-swatch-icon"></i>
                </div>
                <span class="color-value">{{ settings.strokeColor }}</span>
              </div>
            </div>
          </div>

          <!-- Highlight Settings (for highlight tool) -->
          <div 
            v-if="currentTool === 'highlight'" 
            class="context-panel__section"
          >
            <div class="context-panel__section-title">
              {{ $t('annotation.highlightSettings') || 'Highlight Settings' }}
            </div>

            <!-- Opacity -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.opacity') || 'Opacity' }}
              </label>
              <div class="control-with-value">
                <input
                  type="range"
                  min="0"
                  max="100"
                  :value="settings.opacity * 100"
                  @input="handleOpacityChange"
                  class="slider"
                />
                <span class="value-text-inline">{{ Math.round(settings.opacity * 100) }}%</span>
              </div>
              <div class="opacity-preview" :style="{ opacity: settings.opacity, backgroundColor: settings.strokeColor }"></div>
            </div>

            <!-- Color -->
            <div class="control-group">
              <label class="control-label">
                {{ $t('annotation.color') || 'Color' }}
              </label>
              <div class="color-display">
                <div 
                  class="color-swatch-large" 
                  :style="{ backgroundColor: settings.strokeColor }"
                  @click="$emit('open-color-picker')"
                  :title="$t('annotation.selectColor') || 'Click to change color'"
                >
                  <i class="fas fa-palette color-swatch-icon"></i>
                </div>
                <span class="color-value">{{ settings.strokeColor }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Panel Footer (optional tips) -->
        <div class="context-panel__footer" v-if="getToolTip(currentTool)">
          <i class="fas fa-lightbulb"></i>
          <span>{{ getToolTip(currentTool) }}</span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AnnotationTool, type AnnotationToolSettings } from '@/types/annotation';

interface Props {
  currentTool: AnnotationTool;
  settings: AnnotationToolSettings;
  isVisible: boolean;
  anchorElement?: HTMLElement | null;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
  anchorElement: null,
});

const emit = defineEmits<{
  (e: 'setting-change', setting: string, value: any): void;
  (e: 'calibrate-scale'): void;
  (e: 'open-color-picker'): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();

// Panel reference
const panelRef = ref<HTMLElement | null>(null);

// Panel positioning
const panelPosition = ref({
  top: '80px',
  left: '20px',
});

// Scale presets
const scalePresets = [50, 75, 100, 150, 200];

// Measurement units
const measurementUnits = [
  { value: 'meters', label: 'Meters', short: 'm' },
  { value: 'feet', label: 'Feet', short: 'ft' },
  { value: 'inches', label: 'Inches', short: 'in' },
];

// Tool type checks
const isDrawingTool = computed(() => {
  return ['freehand', 'rectangle', 'circle', 'arrow', 'line', 'eraser'].includes(props.currentTool);
});

const isMeasurementTool = computed(() => {
  return ['measure-distance', 'measure-area', 'measure-polygon'].includes(props.currentTool);
});

// Panel classes
const panelClasses = computed(() => ({
  'context-panel--drawing': isDrawingTool.value,
  'context-panel--measurement': isMeasurementTool.value,
  'context-panel--text': props.currentTool === 'text',
  'context-panel--highlight': props.currentTool === 'highlight',
}));

// Tool metadata
const toolMetadata: Record<string, { icon: string; label: string; tip?: string }> = {
  'freehand': { 
    icon: 'fas fa-pencil-alt', 
    label: 'Freehand Drawing',
    tip: 'Click and drag to draw freehand shapes'
  },
  'rectangle': { 
    icon: 'fas fa-square', 
    label: 'Rectangle',
    tip: 'Click and drag to create a rectangle'
  },
  'circle': { 
    icon: 'fas fa-circle', 
    label: 'Circle',
    tip: 'Click and drag to create a circle'
  },
  'arrow': { 
    icon: 'fas fa-arrow-right', 
    label: 'Arrow',
    tip: 'Click to set start point, click again to set end point'
  },
  'line': { 
    icon: 'fas fa-minus', 
    label: 'Line',
    tip: 'Click to set start point, click again to set end point'
  },
  'text': { 
    icon: 'fas fa-font', 
    label: 'Text',
    tip: 'Click to place text on the document'
  },
  'highlight': { 
    icon: 'fas fa-highlighter', 
    label: 'Highlight',
    tip: 'Click and drag to highlight areas'
  },
  'measure-distance': { 
    icon: 'fas fa-ruler', 
    label: 'Measure Distance',
    tip: 'Click to set points, press Enter to finish'
  },
  'measure-area': { 
    icon: 'fas fa-draw-polygon', 
    label: 'Measure Area',
    tip: 'Click to set points, press Enter to close shape'
  },
  'measure-polygon': { 
    icon: 'fas fa-shapes', 
    label: 'Measure Polygon',
    tip: 'Click to set vertices, press Enter to finish'
  },
  'eraser': { 
    icon: 'fas fa-eraser', 
    label: 'Eraser',
    tip: 'Click on annotations to remove them'
  },
};

// Get tool metadata
const getToolIcon = (tool: string): string => {
  return toolMetadata[tool]?.icon || 'fas fa-question';
};

const getToolLabel = (tool: string): string => {
  return t(`annotation.${tool}`) || toolMetadata[tool]?.label || tool;
};

const getToolTip = (tool: string): string | undefined => {
  return toolMetadata[tool]?.tip;
};

// Calculate panel position
const calculatePosition = () => {
  if (!props.anchorElement || !panelRef.value) {
    // Default position
    panelPosition.value = {
      top: '80px',
      left: '20px',
    };
    return;
  }

  const anchorRect = props.anchorElement.getBoundingClientRect();
  const panelRect = panelRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Try to position below the toolbar
  let top = anchorRect.bottom + 8;
  let left = anchorRect.left + 20;

  // Check if panel would overflow right edge
  if (left + panelRect.width > viewportWidth) {
    left = viewportWidth - panelRect.width - 20;
  }

  // Check if panel would overflow bottom edge
  if (top + panelRect.height > viewportHeight) {
    // Position above the toolbar instead
    top = anchorRect.top - panelRect.height - 8;
  }

  // Ensure minimum margins
  left = Math.max(20, left);
  top = Math.max(20, top);

  panelPosition.value = {
    top: `${top}px`,
    left: `${left}px`,
  };
};

// Watch for visibility and tool changes to recalculate position
watch([() => props.isVisible, () => props.currentTool], () => {
  if (props.isVisible) {
    nextTick(() => {
      calculatePosition();
    });
  }
});

// Recalculate on window resize
const handleResize = () => {
  if (props.isVisible) {
    calculatePosition();
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  if (props.isVisible) {
    nextTick(() => {
      calculatePosition();
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

// Event handlers
const handleStrokeWidthChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'strokeWidth', parseInt(target.value));
};

const adjustStrokeWidth = (delta: number) => {
  const newValue = Math.max(1, Math.min(20, props.settings.strokeWidth + delta));
  emit('setting-change', 'strokeWidth', newValue);
};

const handleOpacityChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const opacity = parseInt(target.value) / 100;
  emit('setting-change', 'opacity', opacity);
};

const adjustOpacity = (delta: number) => {
  const currentOpacity = Math.round(props.settings.opacity * 100);
  const newOpacity = Math.max(0, Math.min(100, currentOpacity + delta)) / 100;
  emit('setting-change', 'opacity', newOpacity);
};

const handleFontSizeChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'fontSize', parseInt(target.value));
};

const adjustFontSize = (delta: number) => {
  const newValue = Math.max(8, Math.min(72, props.settings.fontSize + delta));
  emit('setting-change', 'fontSize', newValue);
};

const getCurrentScaleValue = (): number => {
  const currentScale = props.settings.scale || '1:100';
  const parts = currentScale.split(':');
  return parseFloat(parts[1]) || 100;
};

const handleCustomScaleInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = parseFloat(target.value);
  
  if (value && value > 0) {
    const scaleLabel = `1:${value}`;
    emit('setting-change', 'scale', scaleLabel);
    emit('setting-change', 'scaleRatio', value);
  }
};

const handleCustomScaleBlur = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let value = parseFloat(target.value);
  
  if (!value || value < 1) {
    value = 100;
    target.value = '100';
  }
  
  const scaleLabel = `1:${value}`;
  emit('setting-change', 'scale', scaleLabel);
  emit('setting-change', 'scaleRatio', value);
};

const setScale = (value: number) => {
  const scaleLabel = `1:${value}`;
  emit('setting-change', 'scale', scaleLabel);
  emit('setting-change', 'scaleRatio', value);
};

const handleUnitChange = (unit: string) => {
  emit('setting-change', 'measurementUnit', unit);
};

const handleClose = () => {
  emit('close');
};
</script>

<style scoped>
/* Context Panel Base Styles */
.annotation-context-panel {
  position: fixed !important;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 6px 10px rgba(0, 0, 0, 0.1);
  min-width: 280px;
  max-width: 320px;
  z-index: 100000 !important; /* Higher than DocumentAnnotator (99999) */
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header */
.context-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.context-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
}

.context-panel__icon {
  font-size: 16px;
  opacity: 0.9;
}

.context-panel__close {
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.context-panel__close:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Content */
.context-panel__content {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.context-panel__section {
  margin-bottom: 16px;
}

.context-panel__section:last-child {
  margin-bottom: 0;
}

.context-panel__section-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
}

/* Control Groups */
.control-group {
  margin-bottom: 16px;
}

.control-group:last-child {
  margin-bottom: 0;
}

.control-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.control-with-value {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Sliders */
.slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  background: #2563eb;
  transform: scale(1.1);
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  background: #2563eb;
  transform: scale(1.1);
}

/* Value Display */
.control-value-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.value-btn {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
}

.value-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.value-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.value-text {
  flex: 1;
  text-align: center;
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
}

.value-text-inline {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
}

.value-input {
  flex: 1;
  text-align: center;
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  height: 28px;
}

.value-input:focus {
  outline: none;
  border-color: #3b82f6;
}

/* Opacity Preview */
.opacity-preview {
  width: 100%;
  height: 40px;
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid #d1d5db;
  position: relative;
  background-image: 
    linear-gradient(45deg, #ccc 25%, transparent 25%), 
    linear-gradient(-45deg, #ccc 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #ccc 75%), 
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0px;
}

/* Color Display */
.color-display {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-swatch-large {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.color-swatch-large::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(45deg, #ccc 25%, transparent 25%), 
    linear-gradient(-45deg, #ccc 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #ccc 75%), 
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
  z-index: 0;
}

.color-swatch-large:hover {
  border-color: #3b82f6;
  transform: scale(1.05);
}

.color-swatch-icon {
  position: relative;
  z-index: 1;
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  opacity: 0;
  transition: opacity 0.2s;
}

.color-swatch-large:hover .color-swatch-icon {
  opacity: 1;
}

.color-value {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

/* Scale Input */
.scale-input-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}

.scale-prefix {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.scale-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px !important;
  font-weight: 500;
}

.scale-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Scale Presets */
.scale-presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}

.preset-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.preset-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

/* Unit Selector */
.unit-selector {
  display: flex;
  gap: 6px;
}

.unit-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}

.unit-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.unit-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

/* Calibrate Button */
.btn-calibrate {
  width: 100%;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-calibrate:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Footer */
.context-panel__footer {
  padding: 12px 16px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.context-panel__footer i {
  color: #f59e0b;
  margin-top: 2px;
}

/* Transitions */
.context-panel-enter-active,
.context-panel-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.context-panel-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.context-panel-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

/* Scrollbar Styling */
.context-panel__content::-webkit-scrollbar {
  width: 6px;
}

.context-panel__content::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.context-panel__content::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.context-panel__content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .annotation-context-panel {
    min-width: 260px;
    max-width: 90vw;
  }

  .context-panel__content {
    max-height: 50vh;
  }
}
</style>
